import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { useSnackbar } from 'notistack';

const WebSocketContext = createContext();
const WEBSOCKET_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_WEBSOCKET_DEBUG === '1';
const NOTIFIED_RETIREMENTS_KEY = 'notifiedRetirements';

function resolveSocketConfig(url) {
  try {
    const parsed = new URL(url);
    const baseURL = `${parsed.protocol}//${parsed.host}`;
    const socketPath = parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : '/ws';
    return { baseURL, socketPath };
  } catch {
    return { baseURL: url, socketPath: '/ws' };
  }
}

function resolveApiBase(url) {
  try {
    const parsed = new URL(url);
    const protocol = parsed.protocol === 'wss:' ? 'https:' : parsed.protocol === 'ws:' ? 'http:' : parsed.protocol;
    return `${protocol}//${parsed.host}`;
  } catch {
    return 'https://serenity.ing';
  }
}

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    // Return default values instead of throwing error during initialization
    return {
      socket: null,
      isConnected: false,
      retirements: [],
      stats: null,
      lastRetirement: null,
      requestSync: () => console.warn('WebSocket not initialized'),
      checkRetirement: () => console.warn('WebSocket not initialized'),
      addRetirement: () => console.warn('WebSocket not initialized'),
      updateStats: () => console.warn('WebSocket not initialized'),
      connectionStatus: {
        connected: false,
        url: null
      }
    };
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [retirements, setRetirements] = useState([]);
  const [stats, setStats] = useState(null);
  const [lastRetirement, setLastRetirement] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const socketRef = useRef(null);
  const isUnmountedRef = useRef(false);
  const didAnnounceConnectedRef = useRef(false);
  const disconnectNotifiedRef = useRef(false);
  const connectErrorCountRef = useRef(0);
  const reconnectKickTimerRef = useRef(null);

  // --- Notified retirements state, persisted in localStorage ---
  const [notifiedRetirements, setNotifiedRetirements] = useState(() => {
    try {
      const stored = localStorage.getItem(NOTIFIED_RETIREMENTS_KEY);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (e) {
      console.warn('[WebSocket] Failed to load notifiedRetirements from localStorage', e);
    }
    return new Set();
  });
  // Persist notifiedRetirements to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(NOTIFIED_RETIREMENTS_KEY, JSON.stringify(Array.from(notifiedRetirements)));
    } catch (e) {
      console.warn('[WebSocket] Failed to persist notifiedRetirements', e);
    }
  }, [notifiedRetirements]);

  const notifiedRetirementsRef = useRef(notifiedRetirements);
  useEffect(() => {
    notifiedRetirementsRef.current = notifiedRetirements;
  }, [notifiedRetirements]);

  // WebSocket server URL - adjust based on your deployment
  const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || 'wss://serenity.ing/retirement/ws';
  const WEBSOCKET_ENABLED = process.env.REACT_APP_ENABLE_WEBSOCKET !== 'false';
  const { baseURL, socketPath } = resolveSocketConfig(WEBSOCKET_URL);
  const API_BASE = resolveApiBase(WEBSOCKET_URL);

  const debugLog = (...args) => {
    if (WEBSOCKET_DEBUG) {
      console.log('[WebSocket]', ...args);
    }
  };

  const connectSocket = () => {
    if (!WEBSOCKET_ENABLED) {
      debugLog('WebSocket disabled via environment variable');
      return null;
    }

    if (socketRef.current) {
      return socketRef.current;
    }

    debugLog('Attempting to connect to:', WEBSOCKET_URL);

    const connectionOptions = {
      path: socketPath,
      transports: ['polling', 'websocket'],
      timeout: 10000,
      autoConnect: true,
      forceNew: false,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1500,
      reconnectionDelayMax: 10000,
      upgrade: true,
    };

    const newSocket = io(baseURL, connectionOptions);
    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on('connect', () => {
      debugLog('Connected successfully to:', WEBSOCKET_URL);
      debugLog('Socket ID:', newSocket.id);
      setIsConnected(true);
      disconnectNotifiedRef.current = false;
      connectErrorCountRef.current = 0;

      if (reconnectKickTimerRef.current) {
        clearTimeout(reconnectKickTimerRef.current);
        reconnectKickTimerRef.current = null;
      }

      if (!didAnnounceConnectedRef.current) {
        didAnnounceConnectedRef.current = true;
        enqueueSnackbar('🔄 Real-time updates connected', { 
          variant: 'success',
          autoHideDuration: 3000 
        });
      } else {
        enqueueSnackbar('🔄 Real-time updates reconnected', {
          variant: 'info',
          autoHideDuration: 2500,
        });
      }
    });

    newSocket.on('disconnect', (reason) => {
      debugLog('Disconnected:', reason);
      setIsConnected(false);

      if (!isUnmountedRef.current && reason !== 'io client disconnect' && !disconnectNotifiedRef.current) {
        disconnectNotifiedRef.current = true;
        enqueueSnackbar('⚠️ Real-time updates disconnected', {
          variant: 'warning',
          autoHideDuration: 5000
        });
      }

      if (!isUnmountedRef.current && reason !== 'io client disconnect') {
        if (reconnectKickTimerRef.current) {
          clearTimeout(reconnectKickTimerRef.current);
        }
        reconnectKickTimerRef.current = setTimeout(() => {
          if (!isUnmountedRef.current && socketRef.current && !socketRef.current.connected) {
            debugLog('Manual reconnect kick after disconnect');
            socketRef.current.connect();
          }
        }, 2500);
      }
    });

    newSocket.on('connect_error', (error) => {
      debugLog('Connection error:', error?.message || error);
      setIsConnected(false);
      connectErrorCountRef.current += 1;

      // After repeated websocket-start failures on reload, force polling-first fallback and retry.
      if (connectErrorCountRef.current >= 2 && newSocket?.io?.opts) {
        newSocket.io.opts.transports = ['polling', 'websocket'];
      }

      if (!isUnmountedRef.current) {
        if (reconnectKickTimerRef.current) {
          clearTimeout(reconnectKickTimerRef.current);
        }
        reconnectKickTimerRef.current = setTimeout(() => {
          if (!isUnmountedRef.current && socketRef.current && !socketRef.current.connected) {
            debugLog('Manual reconnect kick after connect_error');
            socketRef.current.connect();
          }
        }, 3000);
      }
    });

    newSocket.on('connecting', () => {
      debugLog('Connecting...');
    });

    newSocket.on('reconnect', () => {
      debugLog('Reconnected');
    });

    newSocket.on('reconnect_error', (error) => {
      debugLog('Reconnection error:', error?.message || error);
    });

    newSocket.on('reconnect_failed', () => {
      debugLog('Reconnection failed');
    });

    newSocket.on('newRetirement', (retirement) => {
      const id = retirement.txHash || retirement.tokenId;
      if (!id) return;

      if (!notifiedRetirementsRef.current.has(id)) {
        debugLog('New retirement received:', retirement);
        setLastRetirement(retirement);

        setRetirements((prev) => {
          const exists = prev.some((r) => (r.txHash || r.tokenId) === id);
          if (exists) return prev;
          return [retirement, ...prev];
        });

        enqueueSnackbar(
          `🌱 New retirement: Token #${retirement.tokenId} 1 ton CO₂)`, 
          { 
            variant: 'success',
            autoHideDuration: 8000,
            action: (snackbarId) => {
              return (
              <button
                onClick={() => {
                  const registryTable = document.querySelector('[data-testid="registry-table"]');
                  if (registryTable) {
                    registryTable.scrollIntoView({ behavior: 'smooth' });
                  }
                  closeSnackbar(snackbarId);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                View
              </button>
            );
            }
          }
        );

        setNotifiedRetirements((prevSet) => {
          const newSet = new Set(prevSet);
          newSet.add(id);
          notifiedRetirementsRef.current = newSet;
          return newSet;
        });
      }
    });

    newSocket.on('statsUpdate', (updatedStats) => {
      debugLog('Stats update received:', updatedStats);
      setStats(updatedStats);
    });

    return newSocket;
  };

  const cleanupSocket = (socketToClose = socketRef.current) => {
    if (reconnectKickTimerRef.current) {
      clearTimeout(reconnectKickTimerRef.current);
      reconnectKickTimerRef.current = null;
    }

    if (!socketToClose) return;
    socketToClose.removeAllListeners();
    socketToClose.disconnect();

    if (socketRef.current === socketToClose) {
      socketRef.current = null;
    }

    setSocket((prev) => (prev === socketToClose ? null : prev));
  };

  useEffect(() => {
    isUnmountedRef.current = false;

    if (!WEBSOCKET_ENABLED) {
      debugLog('WebSocket functionality disabled');
      setIsInitialized(true);
      return () => {
        isUnmountedRef.current = true;
      };
    }

    const newSocket = connectSocket();
    setIsInitialized(true);

    return () => {
      isUnmountedRef.current = true;
      cleanupSocket(newSocket);
      setIsConnected(false);
      setIsInitialized(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to request manual sync
  const requestSync = () => {
    const currentSocket = socketRef.current;
    if (currentSocket && isConnected) {
      debugLog('Requesting manual sync');
      currentSocket.emit('requestSync');
      enqueueSnackbar('🔄 Requesting data sync...', { 
        variant: 'info',
        autoHideDuration: 3000 
      });
    }
  };

  // Function to check specific retirement (useful after retirement transaction)
  const checkRetirement = async (tokenId) => {
    try {
      debugLog(`Checking retirement for tokenId ${tokenId}`);
      const response = await fetch(`${API_BASE}/retirement/api/check-retirement/${tokenId}`);
      const result = await response.json();
      
      if (result.found && result.retirement) {
        debugLog(`Manual check found retirement for tokenId ${tokenId}`);
        addRetirement(result.retirement);
        enqueueSnackbar(`✅ Retirement confirmed for Token #${tokenId}`, { 
          variant: 'success',
          autoHideDuration: 5000 
        });
        return result.retirement;
      } else {
        debugLog(`Manual check: ${result.message}`);
        return null;
      }
    } catch (error) {
      debugLog(`Error checking retirement for tokenId ${tokenId}:`, error?.message || error);
      return null;
    }
  };

  // Function to add retirement manually (for fallback scenarios)
  const addRetirement = (retirement) => {
    setRetirements(prev => {
      // Check if retirement already exists
      const exists = prev.some(r => r.tokenId === retirement.tokenId);
      if (!exists) {
        return [retirement, ...prev];
      }
      return prev;
    });
  };

  // Function to update statistics manually (for fallback scenarios)
  const updateStats = (newStats) => {
    setStats(newStats);
  };

  const value = {
    socket,
    isConnected,
    retirements,
    stats,
    lastRetirement,
    requestSync,
    checkRetirement,
    addRetirement,
    updateStats,
    isInitialized,
    connectionStatus: {
      connected: isConnected,
      url: WEBSOCKET_URL
    }
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
